import React, { useState, useEffect, useRef } from 'react';
import { Link, NavLink } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { FaBars, FaTimes, FaUserPlus } from 'react-icons/fa';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [visible, setVisible] = useState(true);
  const navRef = useRef(null);
  const lastScrollY = useRef(0);
  const scrollingThreshold = 100;
  const throttleTimeout = useRef(null);

  useEffect(() => {
    const handleScroll = () => {
      // Prevent multiple scroll events with throttling
      if (throttleTimeout.current) return;
      
      throttleTimeout.current = setTimeout(() => {
        const currentScrollY = window.scrollY;
        
        if (currentScrollY > lastScrollY.current && currentScrollY > scrollingThreshold) {
          setVisible(false);
        } else {
          setVisible(true);
        }
        
        lastScrollY.current = currentScrollY;
        throttleTimeout.current = null;
      }, 100); // Throttle to execute at most once every 100ms
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (throttleTimeout.current) {
        clearTimeout(throttleTimeout.current);
      }
    };
  }, []);

  const menuVariants = {
    open: { opacity: 1, height: 'auto' },
    closed: { opacity: 0, height: 0 }
  };

  return (
    <AnimatePresence>
      <motion.header 
        ref={navRef}
        className="fixed w-full z-40 flex justify-center px-4 optimized-navbar"
        initial={{ y: 0 }}
        animate={{ 
          y: visible ? 0 : -100,
          opacity: visible ? 1 : 0
        }}
        transition={{ 
          duration: 0.3,
          ease: "easeInOut"
        }}
      >
        <div className="glassmorphism-nav-container">
          <div className="container mx-auto px-3 py-1">
            <div className="flex justify-between items-center">
              <Link to="/" className="flex items-center">
                <motion.img 
                  src={`${process.env.PUBLIC_URL}/resources/images/logo_project.png`} 
                  alt="Project San Andreas Roleplay" 
                  className="h-12 md:h-14"
                  style={{ maxWidth: '200px', objectFit: 'contain' }}
                  whileHover={{ scale: 1.03 }}
                  transition={{ duration: 0.2 }}
                />
              </Link>

              {/* Desktop Navigation */}
              <nav className="hidden md:flex items-center md:space-x-5 lg:space-x-8">
                <NavLink to="/" className={({isActive}) => `nav-link ${isActive ? 'active' : ''}`} end>
                  Trang Chủ
                </NavLink>
                <NavLink to="/news" className={({isActive}) => `nav-link ${isActive ? 'active' : ''}`}>
                  Tin Tức
                </NavLink>
                <NavLink to="/servers" className={({isActive}) => `nav-link ${isActive ? 'active' : ''}`}>
                  Máy Chủ
                </NavLink>
                <NavLink to="/guides" className={({isActive}) => `nav-link ${isActive ? 'active' : ''}`}>
                  Hướng Dẫn
                </NavLink>
                <NavLink to="/download" className={({isActive}) => `nav-link ${isActive ? 'active' : ''}`}>
                  Tải Về
                </NavLink>
              </nav>

              <div className="hidden md:flex items-center space-x-4">
                <Link to="/register" className="btn btn-primary flex items-center glass-button text-dark whitespace-nowrap">
                  <FaUserPlus className="mr-2" />
                  <span>Đăng Ký</span>
                </Link>
              </div>

              {/* Mobile Menu Button */}
              <button 
                className="md:hidden text-dark focus:outline-none"
                onClick={() => setIsOpen(!isOpen)}
              >
                {isOpen ? <FaTimes size={22} /> : <FaBars size={22} />}
              </button>
            </div>

            {/* Mobile Navigation */}
            <motion.div
              className="md:hidden overflow-hidden"
              initial="closed"
              animate={isOpen ? "open" : "closed"}
              variants={menuVariants}
              transition={{ duration: 0.3 }}
            >
              <div className={`flex flex-col py-4 mt-4 rounded-lg ${isOpen ? 'glassmorphism-menu' : ''}`}>
                <NavLink to="/" className={({isActive}) => `nav-link py-3 ${isActive ? 'active' : ''}`} onClick={() => setIsOpen(false)} end>
                  Trang Chủ
                </NavLink>
                <NavLink to="/news" className={({isActive}) => `nav-link py-3 ${isActive ? 'active' : ''}`} onClick={() => setIsOpen(false)}>
                  Tin Tức
                </NavLink>
                <NavLink to="/servers" className={({isActive}) => `nav-link py-3 ${isActive ? 'active' : ''}`} onClick={() => setIsOpen(false)}>
                  Máy Chủ
                </NavLink>
                <NavLink to="/guides" className={({isActive}) => `nav-link py-3 ${isActive ? 'active' : ''}`} onClick={() => setIsOpen(false)}>
                  Hướng Dẫn
                </NavLink>
                <NavLink to="/download" className={({isActive}) => `nav-link py-3 ${isActive ? 'active' : ''}`} onClick={() => setIsOpen(false)}>
                  Tải Về
                </NavLink>
                
                <div className="flex flex-col space-y-3 mt-4">
                  <Link to="/register" className="btn btn-primary flex items-center justify-center glass-button" onClick={() => setIsOpen(false)}>
                    <FaUserPlus className="mr-2" />
                    Đăng Ký
                  </Link>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </motion.header>
    </AnimatePresence>
  );
};

export default Navbar;
